import {useCallback,useEffect,useMemo,useRef,useState} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography
} from "@mui/material";
import {
  EmojiEventsRounded,
  ErrorOutlineRounded,
  RefreshRounded,
  SportsSoccerRounded
} from "@mui/icons-material";

const API_BASE="/matches/public/tournaments/nexus-cup-2026";

const COLORS={
  navy:"#06132D",
  navyLight:"#0C2043",
  surface:"#10274C",
  surfaceHover:"#16335C",
  blue:"#78ACFF",
  text:"#F4F7FF",
  muted:"#9EB3D2",
  border:"rgba(174,202,246,.16)",
  borderActive:"rgba(120,172,255,.55)"
};

function TeamLogo({team,size=36}){
  const [failed,setFailed]=useState(false);

  useEffect(()=>{
    setFailed(false);
  },[team?.logo]);

  return(
    <Box
      sx={{
        width:size,
        height:size,
        flexShrink:0,
        display:"flex",
        alignItems:"center",
        justifyContent:"center"
      }}
    >
      {team?.logo&&!failed?(
        <Box
          component="img"
          src={team.logo}
          alt={team.name||"Equipo"}
          loading="lazy"
          onError={()=>setFailed(true)}
          sx={{
            width:"100%",
            height:"100%",
            display:"block",
            objectFit:"contain"
          }}
        />
      ):(
        <SportsSoccerRounded
          sx={{
            fontSize:size*.65,
            color:"#89A5CE"
          }}
        />
      )}
    </Box>
  );
}

function RankingCard({player,position}){
  const first=position===1;

  return(
    <Box
      sx={{
        minWidth:0,
        minHeight:{xs:185,sm:230},
        width:"100%",
        height:"100%",
        display:"flex",
        flexDirection:"column",
        alignItems:"center",
        justifyContent:"center",
        gap:{xs:1.2,sm:1.6},
        p:{xs:2,sm:3},
        borderRadius:{xs:"16px",sm:"20px"},
        border:first
          ?`1px solid ${COLORS.borderActive}`
          :`1px solid ${COLORS.border}`,
        backgroundColor:first
          ?COLORS.surfaceHover
          :COLORS.surface,
        boxShadow:"none",
        transition:"background-color .2s ease,border-color .2s ease",
        "&:hover":{
          backgroundColor:COLORS.surfaceHover,
          borderColor:COLORS.borderActive
        }
      }}
    >
      <Box
        sx={{
          width:{xs:33,sm:38},
          height:{xs:33,sm:38},
          borderRadius:"50%",
          display:"flex",
          alignItems:"center",
          justifyContent:"center",
          backgroundColor:first
            ?"#2D61B1"
            :"rgba(120,172,255,.13)",
          border:first
            ?"1px solid rgba(169,202,255,.4)"
            :"1px solid rgba(174,202,246,.12)"
        }}
      >
        <Typography
          sx={{
            fontSize:{xs:14,sm:16},
            fontWeight:600,
            color:COLORS.text
          }}
        >
          {position}
        </Typography>
      </Box>

      <Typography
        sx={{
          minWidth:0,
          width:"100%",
          fontSize:{xs:12,sm:16},
          fontWeight:600,
          lineHeight:1.4,
          color:COLORS.text,
          textAlign:"center",
          overflowWrap:"anywhere"
        }}
      >
        {player.name}
      </Typography>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{minWidth:0,width:"100%"}}
      >
        <TeamLogo team={player.team} size={32}/>

        <Typography
          sx={{
            minWidth:0,
            fontSize:{xs:10,sm:12},
            lineHeight:1.45,
            color:COLORS.muted,
            textAlign:"center",
            overflowWrap:"anywhere"
          }}
        >
          {player.team.name}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={.8}
        sx={{mt:.5}}
      >
        <SportsSoccerRounded
          sx={{
            fontSize:{xs:19,sm:22},
            color:COLORS.blue
          }}
        />

        <Typography
          sx={{
            fontSize:{xs:30,sm:40},
            fontWeight:600,
            letterSpacing:"-.04em",
            lineHeight:1,
            fontVariantNumeric:"tabular-nums",
            color:COLORS.text
          }}
        >
          {player.goals}
        </Typography>
      </Stack>

      <Typography
        sx={{
          fontSize:11,
          color:COLORS.muted
        }}
      >
        {player.goals===1?"Gol":"Goles"}
      </Typography>
    </Box>
  );
}

function RankingRow({player,position}){
  return(
    <Box
      sx={{
        width:"100%",
        minWidth:0,
        display:"grid",
        gridTemplateColumns:{
          xs:"30px minmax(0,1fr) 55px",
          sm:"48px minmax(0,1fr) 90px"
        },
        alignItems:"center",
        gap:{xs:1,sm:2},
        px:{xs:1.5,sm:3},
        py:{xs:1.5,sm:1.8},
        borderBottom:`1px solid ${COLORS.border}`,
        transition:"background-color .2s ease",
        "&:last-child":{
          borderBottom:"none"
        },
        "&:hover":{
          backgroundColor:"rgba(120,172,255,.06)"
        }
      }}
    >
      <Typography
        sx={{
          fontSize:{xs:13,sm:15},
          fontWeight:600,
          color:COLORS.muted,
          textAlign:"center",
          fontVariantNumeric:"tabular-nums"
        }}
      >
        {position}
      </Typography>

      <Stack
        direction="row"
        alignItems="center"
        spacing={{xs:1.2,sm:1.7}}
        sx={{minWidth:0}}
      >
        <TeamLogo team={player.team} size={38}/>

        <Box sx={{minWidth:0,flex:1}}>
          <Typography
            sx={{
              fontSize:{xs:12,sm:14},
              fontWeight:600,
              color:COLORS.text,
              lineHeight:1.45,
              overflowWrap:"anywhere"
            }}
          >
            {player.name}
          </Typography>

          <Typography
            sx={{
              mt:.2,
              fontSize:{xs:10,sm:12},
              color:COLORS.muted,
              lineHeight:1.45
            }}
          >
            {player.team.name}
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={.7}
      >
        <SportsSoccerRounded
          sx={{
            fontSize:{xs:17,sm:19},
            color:COLORS.blue
          }}
        />

        <Typography
          sx={{
            fontSize:{xs:18,sm:22},
            fontWeight:600,
            color:COLORS.text,
            fontVariantNumeric:"tabular-nums"
          }}
        >
          {player.goals}
        </Typography>
      </Stack>
    </Box>
  );
}

function buildRanking(details){
  const players=new Map();

  for(const detail of details){
    const match=detail?.match;

    if(!match) continue;

    for(const goal of detail.goals||[]){
      if(goal.goal_type==="OWN_GOAL") continue;

      const player=goal.player;
      const team=goal.player_team||goal.team;

      if(!player?.slug||!team?.slug) continue;

      const key=`${team.slug}:${player.slug}`;

      if(!players.has(key)){
        players.set(key,{
          key,
          slug:player.slug,
          name:player.name,
          team:{
            slug:team.slug,
            name:team.name,
            logo:null
          },
          goals:0,
          penalties:0
        });
      }

      const item=players.get(key);

      item.goals+=1;

      if(goal.goal_type==="PENALTY"){
        item.penalties+=1;
      }

      const matchTeam=[
        match.local,
        match.visitor
      ].find(entry=>entry?.slug===team.slug);

      if(matchTeam?.logo){
        item.team.logo=matchTeam.logo;
      }
    }
  }

  return Array.from(players.values()).sort((a,b)=>
    b.goals-a.goals||
    a.name.localeCompare(b.name,"es")
  );
}

export default function TopScorersSection(){
  const [details,setDetails]=useState([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState("");
  const [partialError,setPartialError]=useState(false);

  const controllerRef=useRef(null);

  const loadData=useCallback(async()=>{
    controllerRef.current?.abort();

    const controller=new AbortController();
    controllerRef.current=controller;

    setLoading(true);
    setError("");
    setPartialError(false);

    try{
      const response=await fetch(
        `${API_BASE}/calendar/`,
        {signal:controller.signal}
      );

      if(!response.ok){
        throw new Error("No se pudo consultar el calendario.");
      }

      const calendar=await response.json();

      const matches=Array.from(
        new Map(
          (calendar.matches||[])
            .filter(match=>
              match?.code&&
              ["LIVE","FINISHED","SUSPENDED"].includes(match.status)
            )
            .map(match=>[match.code,match])
        ).values()
      );

      if(controller.signal.aborted) return;

      if(matches.length===0){
        setDetails([]);
        return;
      }

      const responses=await Promise.allSettled(
        matches.map(async match=>{
          const result=await fetch(
            `${API_BASE}/matches/${match.code}/`,
            {signal:controller.signal}
          );

          if(!result.ok){
            throw new Error("No se pudo consultar un partido.");
          }

          return result.json();
        })
      );

      if(controller.signal.aborted) return;

      const successful=responses
        .filter(result=>result.status==="fulfilled")
        .map(result=>result.value);

      if(successful.length===0){
        throw new Error(
          "No se pudieron obtener las estadísticas de los partidos."
        );
      }

      setPartialError(successful.length<matches.length);
      setDetails(successful);
    }catch(err){
      if(controller.signal.aborted||err.name==="AbortError") return;

      setDetails([]);
      setError(
        err.message||"No se pudieron cargar los máximos goleadores."
      );
    }finally{
      if(!controller.signal.aborted){
        setLoading(false);
      }
    }
  },[]);

  useEffect(()=>{
    loadData();

    return()=>{
      controllerRef.current?.abort();
    };
  },[loadData]);

  const ranking=useMemo(
    ()=>buildRanking(details),
    [details]
  );

  const leaders=ranking.slice(0,3);
  const remaining=ranking.slice(3,8);

  return(
    <Box
      component="section"
      aria-label="Máximos goleadores del campeonato"
      sx={{
        position:"relative",
        width:"100%",
        backgroundColor:COLORS.navy,
        backgroundImage:`
          radial-gradient(
            ellipse at 50% 0%,
            rgba(35,83,159,.22) 0%,
            rgba(6,19,45,0) 65%
          )
        `,
        py:{xs:7,sm:9,md:11}
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          px:{xs:2,sm:4,md:5}
        }}
      >
        <Stack
          alignItems="center"
          spacing={1.2}
          sx={{mb:{xs:4,md:6}}}
        >
          <Typography
            component="h2"
            sx={{
              fontSize:{xs:29,sm:36,md:43},
              fontWeight:600,
              letterSpacing:"-.035em",
              lineHeight:1.2,
              color:COLORS.text,
              textAlign:"center"
            }}
          >
            Máximos goleadores
          </Typography>
        </Stack>

        {loading?(
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{minHeight:250}}
          >
            <CircularProgress
              size={32}
              sx={{color:COLORS.blue}}
            />

            <Typography
              sx={{
                fontSize:13,
                color:COLORS.muted,
                textAlign:"center"
              }}
            >
              Cargando goleadores...
            </Typography>
          </Stack>
        ):error?(
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{
              minHeight:220,
              textAlign:"center"
            }}
          >
            <ErrorOutlineRounded
              sx={{
                fontSize:38,
                color:"#FFADB7"
              }}
            />

            <Typography
              sx={{
                fontSize:13,
                color:COLORS.muted
              }}
            >
              {error}
            </Typography>

            <Button
              onClick={loadData}
              startIcon={<RefreshRounded/>}
              sx={{
                px:3,
                py:1.1,
                borderRadius:"999px",
                border:`1px solid ${COLORS.borderActive}`,
                backgroundColor:COLORS.surface,
                color:COLORS.text,
                fontWeight:600,
                textTransform:"none",
                boxShadow:"none",
                "&:hover":{
                  backgroundColor:COLORS.surfaceHover,
                  boxShadow:"none"
                }
              }}
            >
              Reintentar
            </Button>
          </Stack>
        ):ranking.length===0?(
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{minHeight:200}}
          >
            <SportsSoccerRounded
              sx={{
                fontSize:42,
                color:"#7695C4"
              }}
            />

            <Typography
              sx={{
                fontSize:14,
                color:COLORS.muted,
                textAlign:"center"
              }}
            >
              Todavía no hay goles registrados.
            </Typography>
          </Stack>
        ):(
          <>
            {partialError&&(
              <Typography
                sx={{
                  mb:3,
                  fontSize:12,
                  color:"#F6CD8A",
                  textAlign:"center"
                }}
              >
                Algunos partidos no pudieron consultarse. La clasificación puede estar incompleta.
              </Typography>
            )}

            <Box
              sx={{
                display:"grid",
                gridTemplateColumns:{
                  xs:"repeat(2,minmax(0,1fr))",
                  sm:"repeat(3,minmax(0,1fr))"
                },
                gap:{xs:1.5,sm:2.5},
                maxWidth:1000,
                mx:"auto"
              }}
            >
              {leaders.map((player,index)=>(
                <Box
                  key={player.key}
                  sx={{
                    gridColumn:{
                      xs:leaders.length===3&&index===2
                        ?"1 / -1"
                        :"auto",
                      sm:"auto"
                    },
                    width:"100%",
                    maxWidth:{
                      xs:leaders.length===3&&index===2?340:"none",
                      sm:"none"
                    },
                    mx:"auto"
                  }}
                >
                  <RankingCard
                    player={player}
                    position={index+1}
                  />
                </Box>
              ))}
            </Box>

            {remaining.length>0&&(
              <Box
                sx={{
                  maxWidth:1000,
                  mx:"auto",
                  mt:{xs:3,sm:4},
                  border:`1px solid ${COLORS.border}`,
                  borderRadius:"18px",
                  overflow:"hidden",
                  backgroundColor:COLORS.navyLight
                }}
              >
                <Box
                  sx={{
                    display:"grid",
                    gridTemplateColumns:{
                      xs:"30px minmax(0,1fr) 55px",
                      sm:"48px minmax(0,1fr) 90px"
                    },
                    gap:{xs:1,sm:2},
                    px:{xs:1.5,sm:3},
                    py:1.7,
                    backgroundColor:COLORS.surface,
                    borderBottom:`1px solid ${COLORS.border}`
                  }}
                >
                  <Typography
                    sx={{
                      fontSize:11,
                      fontWeight:600,
                      color:COLORS.muted,
                      textAlign:"center"
                    }}
                  >
                    Pos.
                  </Typography>

                  <Typography
                    sx={{
                      fontSize:11,
                      fontWeight:600,
                      color:COLORS.muted
                    }}
                  >
                    Jugador
                  </Typography>

                  <Typography
                    sx={{
                      fontSize:11,
                      fontWeight:600,
                      color:COLORS.muted,
                      textAlign:"center"
                    }}
                  >
                    Goles
                  </Typography>
                </Box>

                {remaining.map((player,index)=>(
                  <RankingRow
                    key={player.key}
                    player={player}
                    position={index+4}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}